import { cn } from '@/lib/utils';
import {
  X,
  Clock,
  Sparkles,
  ArrowRight,
  Bell,
  Activity,
  Calendar,
  Heart,
  Pill,
  Target
} from 'lucide-react';
import type { HealthNudge } from '@/types/health-intelligence-types';

interface HealthNudgeCardProps {
  nudge: HealthNudge;
  onDismiss: (id: string) => void;
  onSnooze: (id: string) => void;
  onAction?: (path: string) => void;
}

const priorityColors = {
  high: 'bg-red-500',
  medium: 'bg-amber-500',
  low: 'bg-green-500',
};

const nudgeTypeIcons = {
  medication: Pill,
  appointment: Calendar,
  vital: Activity,
  lifestyle: Heart,
  screening: Target,
  general: Bell,
};

export default function HealthNudgeCard({
  nudge,
  onDismiss,
  onSnooze,
  onAction,
}: HealthNudgeCardProps) {
  const Icon = nudgeTypeIcons[nudge.type as keyof typeof nudgeTypeIcons] || Bell;

  const handleAction = () => {
    if (nudge.actionPath && onAction) {
      onAction(nudge.actionPath);
    }
  };

  return (
    <div
      className={cn(
        "relative rounded-lg border p-4 transition-all hover:shadow-md",
        nudge.isRead
          ? "bg-white/50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700"
          : "bg-white dark:bg-gray-800 border-blue-200 dark:border-blue-800 shadow-sm"
      )}
    >
      <div className="flex items-start gap-3">
        {/* Priority Indicator */}
        <div className="flex-shrink-0 mt-1">
          <div
            className={cn(
              "h-3 w-3 rounded-full",
              priorityColors[nudge.priority as keyof typeof priorityColors]
            )}
          />
        </div>

        {/* Icon */}
        <div className="flex-shrink-0">
          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 flex items-center justify-center">
            <Icon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-start gap-2">
            <h3 className="font-semibold text-sm text-gray-900 dark:text-white flex-1">
              {nudge.title}
            </h3>
            {nudge.aiGenerated && (
              <div className="flex-shrink-0">
                <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                  <Sparkles className="h-3 w-3" />
                  <span className="text-xs font-medium">AI</span>
                </div>
              </div>
            )}
          </div>

          <p className="text-sm text-gray-600 dark:text-gray-300">
            {nudge.message}
          </p>

          {/* Actions */}
          <div className="flex items-center gap-2 pt-1">
            {nudge.actionLabel && (
              <button
                onClick={handleAction}
                className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 transition-colors"
              >
                {nudge.actionLabel}
                <ArrowRight className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>

        {/* Snooze & Dismiss Buttons */}
        <div className="flex-shrink-0 flex items-start gap-1">
          <button
            onClick={() => onSnooze(nudge.id)}
            className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors"
            title="Snooze"
          >
            <Clock className="h-4 w-4" />
          </button>
          {nudge.dismissible && (
            <button
              onClick={() => onDismiss(nudge.id)}
              className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors"
              title="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
